import { AlertTriangle, Loader2, Save, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  useM3AggiornaImpostazioni,
  useM3EliminaPreferenza,
  useM3Impostazioni,
  useM3Paesi,
  useM3PreferenzeCliente,
  useM3UpsertPaese,
  useM3UpsertPreferenza,
} from "@/hooks/useM3";
import { useMyPermessi } from "@/hooks/usePermessi";
import { cn } from "@/lib/utils";
import type {
  ImpostazioniGlobaliUpdate,
  ModalitaAvviso,
  Paese,
  PaeseUpsert,
  RegimePaese,
  StrategiaSplit,
} from "@/types/api";
import { AgenteSection } from "./AgenteSection";
import { AssociazioniEmailSection } from "./AssociazioniEmailSection";
import { SetDocumentiSection } from "./SetDocumentiSection";
import { TemplateSection } from "./TemplateSection";
import { RegimeTag } from "./shared";

type ImpostazioniTab = "generali" | "paesi" | "template" | "agente" | "email";

const INPUT =
  "h-9 w-full rounded-md border bg-card px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

export function ImpostazioniView() {
  const { data: perms } = useMyPermessi();
  const isStaff = perms?.is_maitime_staff ?? false;
  // I non-staff col permesso granulare vedono SOLO la gestione associazioni
  // email; le altre sezioni restano riservate allo staff Maitime.
  const tabs: [ImpostazioniTab, string][] = isStaff
    ? [
        ["generali", "Generali"],
        ["paesi", "Paesi & Regimi"],
        ["template", "Template"],
        ["agente", "Agente"],
        ["email", "Associazioni email"],
      ]
    : [["email", "Associazioni email"]];
  const [tab, setTab] = useState<ImpostazioniTab>(tabs[0][0]);
  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b">
        {tabs.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              tab === k
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "generali" && (
        <div className="space-y-8">
          <ImpostazioniGlobaliForm />
          <PreferenzeClienteSection />
        </div>
      )}
      {tab === "paesi" && (
        <div className="space-y-8">
          <PaesiRegimiSection />
          <SetDocumentiSection />
        </div>
      )}
      {tab === "template" && <TemplateSection />}
      {tab === "agente" && <AgenteSection />}
      {tab === "email" && <AssociazioniEmailSection />}
    </div>
  );
}

function ImpostazioniGlobaliForm() {
  const { data: imp, isLoading } = useM3Impostazioni();
  const aggiorna = useM3AggiornaImpostazioni();
  const [form, setForm] = useState<ImpostazioniGlobaliUpdate>({});

  useEffect(() => {
    if (imp) {
      setForm({
        anticipo_pre_alert_giorni: imp.anticipo_pre_alert_giorni,
        capienza_standard: imp.capienza_standard,
        capienza_max: imp.capienza_max,
        tempo_nota_a_baia_giorni: imp.tempo_nota_a_baia_giorni,
        modalita_avviso_default: imp.modalita_avviso_default,
        strategia_split_default: imp.strategia_split_default,
        orizzonte_proposta_giorni: imp.orizzonte_proposta_giorni,
        soglia_stralcio_pct: imp.soglia_stralcio_pct,
        soglia_sollecito_giorni: imp.soglia_sollecito_giorni,
      });
    }
  }, [imp]);

  if (isLoading) return <Skeleton className="h-48 w-full" />;

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">Impostazioni globali</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-3xl">
        <NumberField
          label="Capienza standard (pallet)"
          value={form.capienza_standard}
          onChange={(v) => setForm((f) => ({ ...f, capienza_standard: v }))}
        />
        <NumberField
          label="Capienza massima (pallet)"
          value={form.capienza_max}
          onChange={(v) => setForm((f) => ({ ...f, capienza_max: v }))}
        />
        <NumberField
          label="Anticipo pre-alert (giorni)"
          value={form.anticipo_pre_alert_giorni}
          onChange={(v) => setForm((f) => ({ ...f, anticipo_pre_alert_giorni: v }))}
        />
        <NumberField
          label="Tempo nota a baia (giorni)"
          value={form.tempo_nota_a_baia_giorni}
          onChange={(v) => setForm((f) => ({ ...f, tempo_nota_a_baia_giorni: v }))}
        />
        <NumberField
          label="Orizzonte proposta (giorni)"
          value={form.orizzonte_proposta_giorni}
          onChange={(v) => setForm((f) => ({ ...f, orizzonte_proposta_giorni: v }))}
        />
        <NumberField
          label="Soglia stralcio (% del camion)"
          value={form.soglia_stralcio_pct}
          onChange={(v) => setForm((f) => ({ ...f, soglia_stralcio_pct: v }))}
        />
        <NumberField
          label="Soglia sollecito (giorni dall'avviso)"
          value={form.soglia_sollecito_giorni}
          onChange={(v) => setForm((f) => ({ ...f, soglia_sollecito_giorni: v }))}
        />
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Modalità avviso default</span>
          <select
            className={INPUT}
            value={form.modalita_avviso_default ?? "manuale"}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                modalita_avviso_default: e.target.value as ModalitaAvviso,
              }))
            }
          >
            <option value="manuale">Manuale</option>
            <option value="automatico">Automatico</option>
          </select>
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Strategia split default</span>
          <select
            className={INPUT}
            value={form.strategia_split_default ?? "saturazione"}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                strategia_split_default: e.target.value as StrategiaSplit,
              }))
            }
          >
            <option value="saturazione">Saturazione (riempi i camion)</option>
            <option value="conservativa">Conservativa (lascia margine)</option>
          </select>
        </label>
      </div>
      <button
        type="button"
        onClick={() => aggiorna.mutate(form)}
        disabled={aggiorna.isPending}
        className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {aggiorna.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Save className="size-4" />
        )}
        Salva impostazioni
      </button>
      {aggiorna.isSuccess && (
        <span className="ml-2 text-xs text-emerald-700">Salvato.</span>
      )}
    </section>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
}) {
  return (
    <label className="space-y-1 text-xs text-muted-foreground">
      <span>{label}</span>
      <input
        type="number"
        className={INPUT}
        value={value ?? ""}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function PreferenzeClienteSection() {
  const { data: prefs, isLoading } = useM3PreferenzeCliente();
  const upsert = useM3UpsertPreferenza();
  const elimina = useM3EliminaPreferenza();
  const [codice, setCodice] = useState("");
  const [capienza, setCapienza] = useState("");

  function aggiungi() {
    const cod = Number(codice);
    if (!cod) return;
    upsert.mutate(
      {
        clienteCodice: cod,
        body: { capienza_override: capienza ? Number(capienza) : null },
      },
      {
        onSuccess: () => {
          setCodice("");
          setCapienza("");
        },
      }
    );
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">
        Preferenze per cliente
      </h2>
      <p className="text-xs text-muted-foreground">
        Override per singolo cliente. La capienza override cambia la proposta di
        carico per quel cliente.
      </p>

      <div className="flex items-end gap-2 flex-wrap">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Codice cliente</span>
          <input
            className={INPUT}
            style={{ width: 140 }}
            value={codice}
            onChange={(e) => setCodice(e.target.value)}
            placeholder="es. 8020992"
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Capienza override</span>
          <input
            className={INPUT}
            style={{ width: 140 }}
            value={capienza}
            onChange={(e) => setCapienza(e.target.value)}
            placeholder="es. 30"
          />
        </label>
        <button
          type="button"
          onClick={aggiungi}
          disabled={upsert.isPending || !codice}
          className="h-9 rounded-md border px-3 text-sm hover:bg-accent disabled:opacity-50"
        >
          Aggiungi / aggiorna
        </button>
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full max-w-2xl" />
      ) : !prefs || prefs.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nessuna preferenza configurata.</p>
      ) : (
        <div className="max-w-2xl overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Cliente</th>
                <th className="px-3 py-2 text-right">Capienza</th>
                <th className="px-3 py-2 text-right">Anticipo</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {prefs.map((p) => (
                <tr key={p.cliente_codice} className="border-t">
                  <td className="px-3 py-2">
                    {p.cliente_ragione_sociale || `Cliente ${p.cliente_codice}`}{" "}
                    <span className="font-mono text-xs text-muted-foreground">
                      #{p.cliente_codice}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {p.capienza_override ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right">{p.anticipo_giorni ?? "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => elimina.mutate(p.cliente_codice)}
                      className="text-muted-foreground hover:text-red-600"
                      title="Elimina preferenza"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const REGIMI: { value: RegimePaese; label: string }[] = [
  { value: "italia", label: "Nazionale" },
  { value: "ue", label: "Intra CEE" },
  { value: "extra_ue", label: "Extra CEE" },
  { value: "bielorussia", label: "Bielorussia" },
];

function PaesiRegimiSection() {
  const { data: paesi, isLoading } = useM3Paesi();
  const upsert = useM3UpsertPaese();
  const [q, setQ] = useState("");
  const [soloDaVerif, setSoloDaVerif] = useState(false);

  function salva(p: Paese, patch: Partial<PaeseUpsert>) {
    upsert.mutate({
      sigla: p.sigla,
      body: {
        nome: p.nome,
        regime: p.regime,
        da_verificare: p.da_verificare,
        ...patch,
      },
    });
  }

  const visibili = useMemo(() => {
    if (!paesi) return [];
    const t = q.toLowerCase().trim();
    return paesi.filter(
      (p) =>
        (!soloDaVerif || p.da_verificare) &&
        (!t ||
          p.sigla.toLowerCase().includes(t) ||
          (p.nome ?? "").toLowerCase().includes(t))
    );
  }, [paesi, q, soloDaVerif]);

  const nDaVerif = paesi?.filter((p) => p.da_verificare).length ?? 0;

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Paesi & Regimi</h2>
        <p className="text-xs text-muted-foreground">
          Sigla gestionale (colonna «Stato» dell'anagrafica) → regime doganale.
          Guida il tag della proposta e i documenti da generare. I codici «da
          verificare» vanno controllati a mano.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca sigla o paese…"
            className={cn(INPUT, "w-56 pl-8")}
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={soloDaVerif}
            onChange={(e) => setSoloDaVerif(e.target.checked)}
          />
          Solo da verificare ({nDaVerif})
        </label>
        {upsert.isPending && (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full max-w-3xl" />
      ) : (
        <div className="max-w-3xl overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Sigla</th>
                <th className="px-3 py-2 text-left">Paese</th>
                <th className="px-3 py-2 text-left">Regime</th>
                <th className="px-3 py-2 text-right">Clienti</th>
                <th className="px-3 py-2 text-center">Stato</th>
              </tr>
            </thead>
            <tbody>
              {visibili.map((p) => (
                <tr
                  key={p.sigla}
                  className={cn("border-t", p.da_verificare && "bg-amber-50/40")}
                >
                  <td className="px-3 py-1.5 font-mono text-xs">{p.sigla}</td>
                  <td className="px-3 py-1.5">
                    <input
                      key={`${p.sigla}-${p.nome}`}
                      defaultValue={p.nome ?? ""}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v !== (p.nome ?? "")) salva(p, { nome: v || null });
                      }}
                      className="h-7 w-full rounded border bg-card px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <select
                        value={p.regime}
                        onChange={(e) =>
                          salva(p, { regime: e.target.value as RegimePaese })
                        }
                        className="h-7 rounded border bg-card px-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      >
                        {REGIMI.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                      <RegimeTag regime={p.regime} />
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                    {p.n_clienti}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <button
                      type="button"
                      onClick={() => salva(p, { da_verificare: !p.da_verificare })}
                      title={
                        p.da_verificare
                          ? "Segna come verificato"
                          : "Segna come da verificare"
                      }
                      className={cn(
                        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]",
                        p.da_verificare
                          ? "bg-amber-100 text-amber-800"
                          : "text-muted-foreground hover:bg-muted"
                      )}
                    >
                      {p.da_verificare ? (
                        <>
                          <AlertTriangle className="size-3" />
                          da verificare
                        </>
                      ) : (
                        "ok"
                      )}
                    </button>
                  </td>
                </tr>
              ))}
              {visibili.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-xs text-muted-foreground">
                    Nessun paese per «{q}».
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
